/**
 * TemplatesTab — list, create, edit, preview, send-test, clone, and soft-delete
 * custom email templates.  Predefined system templates are shown read-only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Chip, Stack,
  Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, CircularProgress, Alert, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CloneIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import CommEmptyState from './shared/CommEmptyState';
import { commTabHeaderSx, commTableHeadRowSx } from './shared/communicationsStyles';
import TemplateEditorDialog, { EmailTemplate } from './TemplateEditorDialog';

const getToken = () => localStorage.getItem('token') ?? '';

const TemplatesTab: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [isNew, setIsNew] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/communication-templates`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setTemplates(res.data.data ?? []);
    } catch {
      setError('Failed to load templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleCreate = () => {
    setEditingTemplate(null);
    setIsNew(true);
    setEditorOpen(true);
  };

  const handleEdit = (t: EmailTemplate) => {
    setEditingTemplate(t);
    setIsNew(false);
    setEditorOpen(true);
  };

  const handleClone = async (t: EmailTemplate) => {
    try {
      await axios.post(
        `${API_BASE_URL}/communication-templates`,
        {
          template_name: `${t.template_name} (copy)`,
          subject_line: t.subject_line,
          body_html: t.body_html,
          body_text: t.body_text,
          supported_merge_tags: t.supported_merge_tags,
        },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setSnackbar({ open: true, message: 'Template cloned successfully.', severity: 'success' });
      fetchTemplates();
    } catch {
      setSnackbar({ open: true, message: 'Failed to clone template.', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/communication-templates/${deleteTarget.template_id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setDeleteTarget(null);
      fetchTemplates();
      setSnackbar({ open: true, message: 'Template deleted.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete template.', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = () => {
    setEditorOpen(false);
    fetchTemplates();
    setSnackbar({ open: true, message: isNew ? 'Template created.' : 'Template updated.', severity: 'success' });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={commTabHeaderSx}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Email Templates</Typography>
          <Typography variant="body2" color="text.secondary">
            Custom HTML email templates used by round-ups and alerts. Predefined system templates
            are locked — clone them to create a customised version.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          New Template
        </Button>
      </Box>

      {loading && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && templates.length === 0 && (
        <CommEmptyState
          icon={<EmailIcon />}
          heading="No email templates yet"
          description="Create a custom template to control the exact HTML sent by your round-ups and alerts."
          ctaLabel="Create first template"
          onCta={handleCreate}
        />
      )}

      {!loading && templates.length > 0 && (
        <Paper>
          <Table>
            <TableHead>
              <TableRow sx={commTableHeadRowSx}>
                <TableCell>Name</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map(t => (
                <TableRow key={t.template_id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{t.template_name}</Typography>
                    {t.template_key && t.template_key !== 'custom' && (
                      <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                        {t.template_key}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.subject_line}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {t.is_predefined ? (
                      <Chip label="System" size="small" variant="outlined" />
                    ) : (
                      <Chip label="Custom" size="small" color="primary" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(t.updated_at).toLocaleDateString('en-GB')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title={t.is_predefined ? 'View' : 'Edit'}>
                        <IconButton size="small" onClick={() => handleEdit(t)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Clone">
                        <IconButton size="small" onClick={() => handleClone(t)}>
                          <CloneIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!t.is_predefined && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(t)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <TemplateEditorDialog
        open={editorOpen}
        template={editingTemplate}
        isNew={isNew}
        onSave={handleSave}
        onClose={() => setEditorOpen(false)}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete template?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>"{deleteTarget?.template_name}"</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplatesTab;
